import * as z from "zod";

export const IntegrationProviderIdSchema = z.enum(["composio", "pipedream"]);
export const IntegrationProviderConfigSchema = z.discriminatedUnion("provider", [
  z.object({ provider: z.literal("composio"), apiKey: z.string().trim().min(1).max(16384) }),
  z.object({
    provider: z.literal("pipedream"),
    clientId: z.string().trim().min(1).max(512),
    clientSecret: z.string().trim().min(1).max(16384),
    projectId: z.string().trim().min(1).max(512),
    environment: z.enum(["production", "development"]).default("production"),
  }),
]);
export type IntegrationProviderConfig = z.infer<typeof IntegrationProviderConfigSchema>;
export type IntegrationProviderId = z.infer<typeof IntegrationProviderIdSchema>;
export const IntegrationSetupStateSchema = z.object({
  canConfigure: z.boolean(),
  needsSetup: z.boolean(),
  webUrl: z.string().url(),
  providers: z.array(z.object({ id: IntegrationProviderIdSchema, configured: z.boolean() })),
});
export type IntegrationSetupState = z.infer<typeof IntegrationSetupStateSchema>;

/** Composio's own toolkit slug, as its dashboard and API spell it (for example `shopify`). */
const ComposioToolkitSlugSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(/^[a-z0-9][a-z0-9_-]{0,63}$/, "Use the app's Composio slug, like shopify");
/** An auth config created in the Composio dashboard. Not a secret; Composio keeps the app credentials. */
const ComposioAuthConfigIdSchema = z
  .string()
  .trim()
  .regex(/^ac_[A-Za-z0-9_-]{1,128}$/, "Auth config IDs start with ac_");

/**
 * Apps Composio has no managed sign-in for connect through the deployment's own auth config.
 * Each toolkit appears at most once.
 */
export const ComposioAuthConfigsSchema = z
  .array(z.object({ toolkit: ComposioToolkitSlugSchema, authConfigId: ComposioAuthConfigIdSchema }))
  .max(200)
  .superRefine((entries, ctx) => {
    const seen = new Set<string>();
    for (const [index, entry] of entries.entries()) {
      if (seen.has(entry.toolkit)) {
        ctx.addIssue({
          code: "custom",
          path: [index, "toolkit"],
          message: `${entry.toolkit} is listed twice`,
        });
      }
      seen.add(entry.toolkit);
    }
  });
export type ComposioAuthConfigs = z.infer<typeof ComposioAuthConfigsSchema>;
export const ComposioAuthConfigsStateSchema = z.object({ authConfigs: ComposioAuthConfigsSchema });
export type ComposioAuthConfigsState = z.infer<typeof ComposioAuthConfigsStateSchema>;

/** The stored map Composio sessions take as `authConfigs`; invalid entries are dropped. */
export function composioAuthConfigMap(stored: unknown): Record<string, string> {
  const record =
    stored && typeof stored === "object" && !Array.isArray(stored)
      ? (stored as Record<string, unknown>)
      : {};
  const map: Record<string, string> = {};
  for (const [toolkit, authConfigId] of Object.entries(record)) {
    const one = ComposioAuthConfigsSchema.safeParse([{ toolkit, authConfigId }]);
    const entry = one.success ? one.data[0] : undefined;
    if (entry && !(entry.toolkit in map)) map[entry.toolkit] = entry.authConfigId;
  }
  return map;
}

/** The deployment's own Shopify app as admins see it: the secret is write-only. */
export const ShopifyAppStateSchema = z.object({
  clientId: z.string().nullable(),
  secretSet: z.boolean(),
});
export type ShopifyAppState = z.infer<typeof ShopifyAppStateSchema>;

/** An empty client ID removes the app's credentials; an omitted secret keeps the saved one. */
export const ShopifyAppCredentialsInputSchema = z.object({
  clientId: z
    .string()
    .trim()
    .max(128)
    .regex(/^[A-Za-z0-9_-]*$/, "Use the app's client ID"),
  clientSecret: z.string().trim().min(1).max(256).optional(),
});
export type ShopifyAppCredentialsInput = z.infer<typeof ShopifyAppCredentialsInputSchema>;
